import { useMemo, useState } from 'react';
import { FiFileText } from 'react-icons/fi';
import { ModelResults } from '../model/domain/types';
import { ImpactTotals } from '../impact/domain/types';
import { LifetimeProjection } from '../lifetime/domain/types';
import { useImpactMode } from '../impact/context/ImpactModeContext';
import { ImpactMode, ReportProjectConfig, ProfileComparisonData } from './domain/types';
import { useReportData } from './hooks/useReportData';
import { useReportGenerator, ReportStatus } from './hooks/useReportGenerator';

interface GenerateReportButtonProps {
  projectName: string;
  techType: string;
  capacityMw: number;
  location: string;
  analysisYear: number;
  isFutureProject: boolean;
  codDate?: string;
  customerName?: string;
  county?: string;
  existingProjectFlag?: boolean;
  modelResults: ModelResults;
  impactTotals: ImpactTotals;
  lifetimeProjection: LifetimeProjection | null;
  impactMode?: ImpactMode;
  latitude?: number;
  longitude?: number;
  eiaBaCode?: string;
  nercCode?: string;
  state?: string;
  profileComparison?: ProfileComparisonData;
}

const STATUS_LABELS: Record<ReportStatus, string> = {
  idle: '',
  charts: 'Rendering charts...',
  rendering: 'Building PDF...',
  done: 'Download started!',
  error: 'Generation failed',
};

// The two report scopes the user can generate. C2G is the full-lifecycle
// (headline) report; Operations excludes manufacturing/infrastructure.
const SCOPES: { value: ImpactMode; label: string; title: string }[] = [
  {
    value: 'ctg',
    label: 'Cradle-to-Grave',
    title: 'Full life cycle, including manufacturing and infrastructure',
  },
  {
    value: 'ops',
    label: 'Operations',
    title: 'Operational impacts only (excludes manufacturing and infrastructure)',
  },
];

export function GenerateReportButton({
  projectName,
  techType,
  capacityMw,
  location,
  analysisYear,
  isFutureProject,
  codDate,
  customerName,
  county,
  existingProjectFlag,
  modelResults,
  impactTotals,
  lifetimeProjection,
  impactMode: impactModeProp,
  latitude,
  longitude,
  eiaBaCode,
  nercCode,
  state,
  profileComparison,
}: GenerateReportButtonProps) {
  const { mode: contextMode } = useImpactMode();
  // Default the report scope to the page's current lens, but let the user
  // pick explicitly before generating.
  const defaultScope: ImpactMode = impactModeProp ?? contextMode ?? 'ctg';
  const [scope, setScope] = useState<ImpactMode>(defaultScope);

  const projectConfig = useMemo<ReportProjectConfig | undefined>(() => {
    if (latitude == null || longitude == null) return undefined;
    return { latitude, longitude, eiaBaCode, nercCode, state };
  }, [latitude, longitude, eiaBaCode, nercCode, state]);

  const reportData = useReportData({
    projectName,
    techType,
    capacityMw,
    location,
    analysisYear,
    isFutureProject,
    codDate,
    customerName,
    county,
    existingProjectFlag,
    modelResults,
    impactTotals,
    lifetimeProjection,
    impactMode: scope,
    projectConfig,
    profileComparison,
  });

  const { generate, status, error } = useReportGenerator();

  const isWorking = status === 'charts' || status === 'rendering';

  const handleClick = async () => {
    if (!reportData || isWorking) return;
    await generate(reportData);
  };

  return (
    <div className="inline-flex items-center gap-3">
      {/* Report scope selector — Cradle-to-Grave vs Operations */}
      <div
        className="inline-flex items-center bg-surface-600 rounded-md p-0.5"
        role="group"
        aria-label="Report scope"
      >
        {SCOPES.map((s) => {
          const active = scope === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setScope(s.value)}
              disabled={isWorking}
              aria-pressed={active}
              title={s.title}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                active
                  ? 'bg-surface-500 text-text-primary shadow-sm'
                  : 'text-text-muted hover:text-text-secondary'
              } ${isWorking ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      <button
        onClick={handleClick}
        disabled={isWorking || !reportData}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors
          ${
            isWorking
              ? 'bg-surface-600 text-text-muted cursor-wait'
              : 'border border-quantum-500/50 text-quantum-400 hover:bg-quantum-500/10'
          }
        `}
      >
        {isWorking ? (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <FiFileText size={16} />
        )}
        {isWorking
          ? STATUS_LABELS[status]
          : `Generate ${scope === 'ops' ? 'Operations' : 'Cradle-to-Grave'} Report`}
      </button>
      {status === 'done' && (
        <span className="text-xs text-quantum-400">PDF downloaded</span>
      )}
      {status === 'error' && (
        <span className="text-xs text-health-400" title={error || undefined}>
          {error || 'Unknown error'}
        </span>
      )}
    </div>
  );
}
